import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  getSaleDepositEditRequests,
  reviewSaleDepositEditRequest,
} from "../../services";
import {
  SaleDepositEditRequest,
  SaleDepositEditRequestFilter,
} from "../../types/sale-deposit-edit-request.type";
import { formatDateTimeBkk } from "../../utils/date";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { featureAccess } from "../../permissions/access";
import ReportPagination from "../Report/ReportList/Pagination";
import TableSkeletonRows from "../SaleDepositReport/TableSkeletonRows";
import { formatBusinessDate } from "../SaleDepositReport/saleCenterClosing.utils";
import DecisionDialog, { EditRequestDecision } from "./DecisionDialog";
import {
  EDIT_REQUEST_STATUS_CHIP_COLOR,
  EDIT_REQUEST_STATUS_LABEL,
} from "./editRequest.utils";

const LIMIT = 10;

/** Columns every reader sees; จัดการ rides on top for the grade that may decide. */
const BASE_COLUMN_COUNT = 7;

/**
 * How wide the เหตุผล cell is allowed to get before it clamps.
 *
 * A reason is free text and can run for paragraphs. Left alone it would push the table wider
 * than the viewport and put the จัดการ buttons off the right-hand edge, so it wraps to two
 * lines, clamps, and keeps the whole text a hover away.
 */
const REASON_COLUMN_WIDTH = 280;

const REASON_CLAMP_SX = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
} as const;

/** The row being answered, and which way — the dialog is mounted only while this is set. */
interface DecisionTarget {
  request: SaleDepositEditRequest;
  decision: EditRequestDecision;
}

interface EditRequestTableProps {
  /** Owned by the page, so the filter row and this list cannot disagree about the query. */
  filter: SaleDepositEditRequestFilter;
  /**
   * Fired only once the server has accepted a decision. The tab above carries a badge of
   * requests still waiting, and this row has just left that count; a refused decision has
   * changed nothing, so it stays silent.
   */
  onRequestDecided?: () => void;
}

/**
 * The คำขอแก้ไขยอดโอน queue.
 *
 * Ordered by the server — pending first, then newest — because the point of the screen is
 * the supervisor's inbox, and re-sorting it here would only fight that.
 *
 * Everyone in the review cohort reads it: the branch needs to see where its own request
 * stands, which is most of why the list exists at all. Only the head-office accounting
 * SUPERVISOR gets the จัดการ column, the same exclusive grade that closed the branch-day
 * this request wants reopened.
 */
export default function EditRequestTable({
  filter,
  onRequestDecided,
}: Readonly<EditRequestTableProps>) {
  const role = useUserRole();
  const canDecide = featureAccess.saleCenterClosing(role);

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(LIMIT);
  const [rows, setRows] = useState<SaleDepositEditRequest[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [decisionTarget, setDecisionTarget] = useState<DecisionTarget | null>(
    null,
  );
  const [saving, setSaving] = useState<boolean>(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getSaleDepositEditRequests(page, limit, filter);
      setRows(data?.rows ?? []);
      setTotalCount(data?.count ?? 0);
    } catch {
      // Reported by the interceptor already. Clear the list rather than leaving the previous
      // filter's requests under the new filter's heading.
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, filter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Back to the first page whenever the filter changes the result set.
  useEffect(() => {
    setPage(1);
  }, [filter]);

  // Curried so each row binds its own request without an inline arrow in JSX.
  const openApproval = (request: SaleDepositEditRequest) => () =>
    setDecisionTarget({ request, decision: "approved" });
  const openRejection = (request: SaleDepositEditRequest) => () =>
    setDecisionTarget({ request, decision: "rejected" });
  const closeDecision = () => setDecisionTarget(null);

  const handleConfirmDecision = async (reviewNote: string | null) => {
    if (!decisionTarget) return;
    setSaving(true);
    try {
      await reviewSaleDepositEditRequest(decisionTarget.request.uuid, {
        status: decisionTarget.decision,
        review_note: reviewNote,
      });
      setDecisionTarget(null);
      onRequestDecided?.();
    } catch {
      // Refused (someone answered it first) and already surfaced. The dialog stays open so a
      // typed note is not lost, and the reload below shows what the queue really holds.
    } finally {
      setSaving(false);
      await fetchRequests();
    }
  };

  const columnCount = canDecide ? BASE_COLUMN_COUNT + 1 : BASE_COLUMN_COUNT;

  return (
    <Paper>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table>
          <TableHead
            sx={{
              "& th": {
                bgcolor: (theme) => theme.palette.background.default,
                color: (theme) => theme.palette.gray.main,
                fontWeight: 600,
              },
            }}
          >
            <TableRow sx={{ whiteSpace: "nowrap" }}>
              <TableCell align="center">วันที่ทำรายการ</TableCell>
              <TableCell align="center">สาขา</TableCell>
              <TableCell align="center">สายส่ง</TableCell>
              <TableCell>ผู้ขอ</TableCell>
              <TableCell>วันที่ส่งคำขอ</TableCell>
              <TableCell>เหตุผลที่ขอแก้ไข</TableCell>
              <TableCell align="center">สถานะคำขอ</TableCell>
              {canDecide && <TableCell align="center">จัดการ</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableSkeletonRows rowCount={limit} columnCount={columnCount} />
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบคำขอตามเงื่อนไข
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((request, index) => {
                const isPending = request.status === "pending";
                const reviewedAtLabel = formatDateTimeBkk(request.reviewed_at);

                return (
                  <TableRow
                    key={request.uuid}
                    sx={{
                      bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                      "& td, & th": { padding: "8px" },
                    }}
                  >
                    <TableCell align="center">
                      {formatBusinessDate(request.business_date)}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {request.branch_code || "-"}
                      </Typography>
                      {request.branch_name && (
                        <Typography variant="caption" color="text.secondary">
                          {request.branch_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">{request.route_code}</TableCell>
                    <TableCell>{request.requested_by_name || "-"}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {formatDateTimeBkk(request.requested_at)} น.
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: REASON_COLUMN_WIDTH }}>
                      <Tooltip title={request.reason}>
                        <Typography variant="body2" sx={REASON_CLAMP_SX}>
                          {request.reason}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        color={EDIT_REQUEST_STATUS_CHIP_COLOR[request.status]}
                        label={EDIT_REQUEST_STATUS_LABEL[request.status]}
                      />
                      {/* Who answered, and what they said — the branch reads this row to
                          find out whether to correct the reason and ask again. */}
                      {!isPending && (
                        <Stack sx={{ mt: 0.5, lineHeight: 1.3 }}>
                          {request.reviewed_by_name && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              lineHeight={1.3}
                            >
                              {request.reviewed_by_name}
                            </Typography>
                          )}
                          {reviewedAtLabel && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              lineHeight={1.3}
                            >
                              {reviewedAtLabel} น.
                            </Typography>
                          )}
                          {request.review_note && (
                            <Tooltip title={request.review_note}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                lineHeight={1.3}
                                sx={REASON_CLAMP_SX}
                              >
                                {request.review_note}
                              </Typography>
                            </Tooltip>
                          )}
                        </Stack>
                      )}
                    </TableCell>
                    {canDecide && (
                      <TableCell align="center">
                        {/* An answered request is terminal — the server refuses a second
                            decision — so the buttons go rather than sit there disabled. */}
                        {isPending ? (
                          <Box
                            display="flex"
                            justifyContent="center"
                            gap={0.5}
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleOutlineIcon />}
                              onClick={openApproval(request)}
                            >
                              อนุมัติ
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<HighlightOffIcon />}
                              onClick={openRejection(request)}
                            >
                              ปฏิเสธ
                            </Button>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            ดำเนินการแล้ว
                          </Typography>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <ReportPagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />

      {decisionTarget && (
        <DecisionDialog
          request={decisionTarget.request}
          decision={decisionTarget.decision}
          saving={saving}
          onClose={closeDecision}
          onConfirm={handleConfirmDecision}
        />
      )}
    </Paper>
  );
}
